import * as React from 'react';
import { useState } from 'react';
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton,
    TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Switch } from '../../components/switch/Switch.tsx';
import { errorText, type MealBody, type MealType, type ShabbatVenue, type VendorMeal } from '../../services/shabbat/shabbatService.ts';
import { addDays, mealName, nowLocal, shabbatService, weekdayOf } from './shabbatShared.tsx';

type Draft = {
    type: MealType;
    date: string;
    startTime: string;
    menu: string;
    seats: string;
    adultPrice: string;
    childPrice: string;
    byTime: boolean;
    closesAtDate: string;
    closesAtTime: string;
    byCount: boolean;
    closesAtGuests: string;
    acceptDonations: boolean;
};

const MENU_MAX = 2000;
const PRICE = /^\d+(\.\d{1,2})?$/;

/** A local wall-clock moment moved by whole hours: "2026-10-16" "18:30" -2 gives "2026-10-16" "16:30". */
const shiftHours = (date: string, time: string, hours: number): { date: string; time: string } => {
    const t = new Date(`${date}T${time}:00Z`);
    t.setUTCHours(t.getUTCHours() + hours);
    return { date: t.toISOString().slice(0, 10), time: t.toISOString().slice(11, 16) };
};

/**
 * Where "At a set time" starts for a new meal: Thursday 18:00 before it, as
 * the design proposes - unless that has already passed, then two hours before
 * the meal starts. Never a time behind us: Thursday 18:00 used to be the
 * default even on a Thursday evening, and the meal was published closed.
 * Null when both have passed; the guest number is offered instead.
 */
const defaultClose = (date: string, startTime: string, timeZone: string): { date: string; time: string } | null => {
    if (!date || !startTime) return null;
    const now = nowLocal(timeZone);
    const start = `${date}T${startTime}`;
    const thursday = addDays(date, -((weekdayOf(date) - 4 + 7) % 7));
    for (const c of [{ date: thursday, time: '18:00' }, shiftHours(date, startTime, -2)]) {
        const at = `${c.date}T${c.time}`;
        if (at > now && at < start) return c;
    }
    return null;
};

/** The close rule a new meal starts with, given its date and start. */
const closeDefaults = (date: string, startTime: string, seats: string, timeZone: string): Partial<Draft> => {
    const c = defaultClose(date, startTime, timeZone);
    return c
        ? { byTime: true, closesAtDate: c.date, closesAtTime: c.time }
        : { byTime: false, byCount: true, closesAtGuests: seats || '40' };
};

/** A new meal starts on this Shabbat's Friday at 18:30, with a close time that has not passed. */
const blank = (friday: string, timeZone: string): Draft => ({
    type: 'FRIDAY_DINNER',
    date: friday,
    startTime: '18:30',
    menu: '',
    seats: '40',
    adultPrice: '25',
    childPrice: '12',
    byTime: true,
    closesAtDate: addDays(friday, -1),
    closesAtTime: '18:00',
    byCount: false,
    closesAtGuests: '40',
    acceptDonations: true,
    ...closeDefaults(friday, '18:30', '40', timeZone),
});

/** The close time the host has picked, when it has already passed and is not the one the meal already had. */
const pastClose = (d: Draft, timeZone: string, original: VendorMeal | null): 'new' | 'kept' | null => {
    if (!d.byTime || !d.closesAtDate || !d.closesAtTime) return null;
    if (`${d.closesAtDate}T${d.closesAtTime}` > nowLocal(timeZone)) return null;
    const kept = !!original && original.closesAtDate === d.closesAtDate && original.closesAtTime === d.closesAtTime;
    return kept ? 'kept' : 'new';
};

const PAST_CLOSE = 'This time has already passed, so registration would close straight away. Pick a later time.';

const fromMeal = (m: VendorMeal): Draft => ({
    type: m.type,
    date: m.date,
    startTime: m.startTime,
    menu: m.menu ?? '',
    seats: String(m.seats),
    adultPrice: String(m.adultPrice),
    childPrice: String(m.childPrice),
    byTime: !!m.closesAtDate,
    closesAtDate: m.closesAtDate ?? addDays(m.date, m.type === 'FRIDAY_DINNER' ? -1 : -2),
    closesAtTime: m.closesAtTime ?? '18:00',
    byCount: m.closesAtGuests != null,
    closesAtGuests: String(m.closesAtGuests ?? m.seats),
    acceptDonations: m.acceptDonations,
});

/**
 * The same checks the server makes (API.md, meal body), so a host sees the
 * problem next to the form instead of after a round trip. The server still
 * decides: its message is shown when it refuses anyway.
 */
const validate = (d: Draft, timeZone: string, booked: number, original: VendorMeal | null): string | null => {
    if (!d.date || !d.startTime) return 'Pick a date and start time.';
    const day = weekdayOf(d.date);
    if (d.type === 'FRIDAY_DINNER' && day !== 5) return 'A Friday night dinner has to be on a Friday.';
    if (d.type === 'SHABBAT_LUNCH' && day !== 6) return 'A Shabbat lunch has to be on a Saturday.';
    if (`${d.date}T${d.startTime}` <= nowLocal(timeZone)) return 'This meal would start in the past.';
    const seats = Number(d.seats);
    if (!Number.isInteger(seats) || seats < 1) return 'Seats must be at least 1.';
    if (booked > 0 && seats < booked) return `${booked} guests are already registered, so seats cannot go below ${booked}.`;
    if (!PRICE.test(d.adultPrice.trim()) || !PRICE.test(d.childPrice.trim())) return 'Prices must be 0 or more, with at most 2 decimals.';
    if (!d.byTime && !d.byCount) return 'Choose at least one way for registration to close.';
    if (d.byTime) {
        if (!d.closesAtDate || !d.closesAtTime) return 'Pick when registration closes.';
        if (`${d.closesAtDate}T${d.closesAtTime}` >= `${d.date}T${d.startTime}`) return 'Registration has to close before the meal starts.';
        // The server refuses a new close time in the past; a closed meal keeps its own.
        if (pastClose(d, timeZone, original) === 'new') return PAST_CLOSE;
    }
    if (d.byCount) {
        const n = Number(d.closesAtGuests);
        if (!Number.isInteger(n) || n < 1) return 'Enter the number of guests that closes registration.';
        if (n > seats) return 'The closing number cannot be more than the seats.';
    }
    if (d.menu.length > MENU_MAX) return `The menu is too long (at most ${MENU_MAX} characters).`;
    return null;
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151', mb: 1 }}>{children}</Typography>
);

type Props = {
    venue: ShabbatVenue;
    friday: string;
    meal: VendorMeal | null;
    onClose: () => void;
    onSaved: (message: string) => void;
};

/** New meal / Edit meal: the right-hand drawer from the design. */
export const MealEditor: React.FC<Props> = ({ venue, friday, meal, onClose, onSaved }) => {
    const [d, setD] = useState<Draft>(() => (meal ? fromMeal(meal) : blank(friday, venue.timeZone)));
    // Until the host touches the close rule, a new meal's close time follows its date and start.
    const [closeTouched, setCloseTouched] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const booked = meal ? meal.guests + meal.pendingGuests : 0;
    const currencySign = venue.currency === 'EUR' ? '€' : venue.currency;

    const up = (patch: Partial<Draft>) => {
        setD(prev => {
            const next = { ...prev, ...patch };
            const moved = 'date' in patch || 'startTime' in patch || 'type' in patch;
            return !meal && !closeTouched && moved
                ? { ...next, ...closeDefaults(next.date, next.startTime, next.seats, venue.timeZone) }
                : next;
        });
        setError(null);
    };
    const CLOSE_KEYS: (keyof Draft)[] = ['closesAtDate', 'closesAtTime', 'closesAtGuests'];
    const on = (key: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (CLOSE_KEYS.includes(key)) setCloseTouched(true);
        up({ [key]: e.target.value } as Partial<Draft>);
    };
    const closeState = pastClose(d, venue.timeZone, meal);

    const pickType = (type: MealType) => {
        if (type === d.type) return;
        // Moves the untouched defaults with the meal: dinner is Friday 18:30, lunch Saturday 12:00.
        const sat = weekdayOf(d.date) === 5 ? addDays(d.date, 1) : d.date;
        const fri = weekdayOf(d.date) === 6 ? addDays(d.date, -1) : d.date;
        const defaultTime = d.startTime === '18:30' || d.startTime === '12:00';
        up({
            type,
            date: type === 'SHABBAT_LUNCH' ? sat : fri,
            startTime: defaultTime ? (type === 'FRIDAY_DINNER' ? '18:30' : '12:00') : d.startTime,
        });
    };

    const save = async () => {
        const problem = validate(d, venue.timeZone, booked, meal);
        if (problem) { setError(problem); return; }
        const body: MealBody = {
            locationId: venue.id,
            type: d.type,
            date: d.date,
            startTime: d.startTime,
            menu: d.menu.trim(),
            seats: Number(d.seats),
            adultPrice: Number(d.adultPrice),
            childPrice: Number(d.childPrice),
            closesAtDate: d.byTime ? d.closesAtDate : null,
            closesAtTime: d.byTime ? d.closesAtTime : null,
            closesAtGuests: d.byCount ? Number(d.closesAtGuests) : null,
            acceptDonations: d.acceptDonations,
        };
        setSaving(true);
        try {
            if (meal) await shabbatService.updateMeal(meal.id, body);
            else await shabbatService.createMeal(body);
            onSaved(meal ? 'Meal updated' : 'Meal published. Guests can register in the Vello app.');
        } catch (e) {
            setError(errorText(e, 'The meal could not be saved. Try again.'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!meal) return;
        setSaving(true);
        try {
            const r = await shabbatService.deleteMeal(meal.id);
            const n = r?.cancelledRegistrations ?? 0;
            onSaved(n > 0 ? `Meal deleted. ${n} registration${n === 1 ? '' : 's'} cancelled and refunded.` : 'Meal deleted');
        } catch (e) {
            setConfirmDelete(false);
            setError(errorText(e, 'The meal could not be deleted. Try again.'));
        } finally {
            setSaving(false);
        }
    };

    const box = { height: 42, '& .MuiInputBase-root': { height: 42, fontSize: 14 } };
    const checkRow = (checked: boolean, toggle: () => void, title: string, help: string, body: React.ReactNode) => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 1.75, py: 1.5 }}>
            <Checkbox checked={checked} onChange={toggle} color="secondary" sx={{ p: 0, mt: '1px' }} inputProps={{ 'aria-label': title }} />
            <Box sx={{ flex: 1 }}>
                <Typography onClick={toggle} sx={{ fontSize: 14, fontWeight: 700, color: '#111827', cursor: 'pointer' }}>{title}</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.25 }}>{help}</Typography>
                {checked && <Box sx={{ mt: 1.25 }}>{body}</Box>}
            </Box>
        </Box>
    );

    return (
        <Drawer anchor="right" open onClose={saving ? undefined : onClose} PaperProps={{ sx: { width: 460, maxWidth: '100%' } }}>
            <Box sx={{ height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5, px: 2.75, borderBottom: '1px solid #E5E7EB' }}>
                <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 700 }}>{meal ? 'Edit meal' : 'New Shabbat meal'}</Typography>
                <IconButton aria-label="Close" onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.75, pt: 2.5, pb: 3.5, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                <Box>
                    <Label>Meal</Label>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {(['FRIDAY_DINNER', 'SHABBAT_LUNCH'] as MealType[]).map(type => {
                            const sel = d.type === type;
                            return (
                                <Box
                                    key={type}
                                    component="button"
                                    type="button"
                                    onClick={() => pickType(type)}
                                    sx={{
                                        flex: 1, height: 40, borderRadius: '10px', fontSize: 14, fontFamily: 'inherit', cursor: 'pointer',
                                        fontWeight: sel ? 700 : 400, bgcolor: sel ? '#24BBAA' : '#fff', color: sel ? '#fff' : '#374151',
                                        border: sel ? '1px solid #24BBAA' : '1px solid #D1D5DB',
                                    }}
                                >
                                    {mealName(type)}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Box><Label>Date</Label><TextField type="date" fullWidth size="small" value={d.date} onChange={on('date')} sx={box} /></Box>
                    <Box><Label>Starts</Label><TextField type="time" fullWidth size="small" value={d.startTime} onChange={on('startTime')} sx={box} /></Box>
                </Box>

                <Box>
                    <Label>Menu</Label>
                    <TextField
                        multiline minRows={3} fullWidth size="small" placeholder="What will be served"
                        value={d.menu} onChange={on('menu')} inputProps={{ maxLength: MENU_MAX }}
                        sx={{ '& .MuiInputBase-root': { fontSize: 14, padding: 0 } }}
                    />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                    <Box><Label>Seats</Label><TextField type="number" fullWidth size="small" value={d.seats} onChange={on('seats')} inputProps={{ min: 1 }} sx={box} /></Box>
                    <Box>
                        <Label>Adult {currencySign}</Label>
                        <TextField type="number" fullWidth size="small" value={d.adultPrice} onChange={on('adultPrice')} inputProps={{ min: 0, step: 0.5, 'aria-label': 'Adult price' }} sx={box} />
                    </Box>
                    <Box>
                        <Label>Child {currencySign}</Label>
                        <TextField type="number" fullWidth size="small" value={d.childPrice} onChange={on('childPrice')} inputProps={{ min: 0, step: 0.5, 'aria-label': 'Child price' }} sx={box} />
                    </Box>
                </Box>

                <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151', mb: 0.5 }}>Registration closes</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mb: 1 }}>Pick one or both. Whichever happens first closes registration.</Typography>
                    <Box sx={{ border: '1px solid #D1D5DB', borderRadius: '10px', overflow: 'hidden' }}>
                        {checkRow(d.byTime, () => { setCloseTouched(true); up({ byTime: !d.byTime }); }, 'At a set time',
                            'Guests can register and cancel until this moment.',
                            <Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField type="date" size="small" value={d.closesAtDate} onChange={on('closesAtDate')} sx={{ flex: 1 }}
                                        error={closeState === 'new'} inputProps={{ 'aria-label': 'Registration closes on' }} />
                                    <TextField type="time" size="small" value={d.closesAtTime} onChange={on('closesAtTime')} sx={{ width: 130 }}
                                        error={closeState === 'new'} inputProps={{ 'aria-label': 'Registration closes at' }} />
                                </Box>
                                {closeState === 'new' && (
                                    <Typography data-close-warning="past" role="alert" sx={{ fontSize: 12.5, color: '#DC2626', mt: 0.75 }}>
                                        {PAST_CLOSE}
                                    </Typography>
                                )}
                                {closeState === 'kept' && (
                                    <Typography data-close-warning="closed" sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.75 }}>
                                        Registration closed at this time. You can still change the other details.
                                    </Typography>
                                )}
                            </Box>)}
                        <Box sx={{ borderTop: '1px solid #E5E7EB' }} />
                        {checkRow(d.byCount, () => { setCloseTouched(true); up({ byCount: !d.byCount }); }, 'When guests reach a number',
                            'Closes automatically once this many guests are registered. Without a set time, cancellations stay free until Thursday 18:00.',
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <TextField type="number" size="small" value={d.closesAtGuests} onChange={on('closesAtGuests')} inputProps={{ min: 1 }} sx={{ width: 110 }} />
                                <Typography sx={{ fontSize: 14, color: '#374151' }}>guests</Typography>
                            </Box>)}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Switch checked={d.acceptDonations} onChange={(_: any, c: boolean) => up({ acceptDonations: c })} inputProps={{ 'aria-label': 'Accept extra donations' }} />
                    <Box sx={{ flex: 1, cursor: 'pointer' }} onClick={() => up({ acceptDonations: !d.acceptDonations })}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Accept extra donations</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.25 }}>Guests can add {currencySign}18 / {currencySign}36 / {currencySign}72 when registering.</Typography>
                    </Box>
                </Box>

                {meal && booked > 0 && (
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>
                        Guests already registered keep the price they paid. Changes to the price apply to new registrations.
                    </Typography>
                )}

                {error && <Typography role="alert" sx={{ fontSize: 13.5, color: '#DC2626' }}>{error}</Typography>}
            </Box>

            <Box sx={{ flexShrink: 0, borderTop: '1px solid #E5E7EB', px: 2.75, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {meal && (
                    <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmDelete(true)} disabled={saving} sx={{ fontWeight: 600 }}>
                        Delete meal
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" onClick={onClose} disabled={saving} sx={{ borderRadius: '20px', color: '#374151', borderColor: '#D1D5DB' }}>Cancel</Button>
                <Button variant="contained" color="secondary" onClick={save} disabled={saving} sx={{ borderRadius: '20px', color: '#fff', px: 2.75 }}>
                    {saving ? 'Saving…' : meal ? 'Save changes' : 'Publish meal'}
                </Button>
            </Box>

            <Dialog open={confirmDelete} onClose={() => !saving && setConfirmDelete(false)} PaperProps={{ sx: { borderRadius: '16px', maxWidth: 420 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete this meal?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14.5, color: '#374151' }}>
                        {meal && meal.registrations > 0
                            ? `All ${meal.registrations} registration${meal.registrations === 1 ? '' : 's'} (${meal.guests} guest${meal.guests === 1 ? '' : 's'}) will be cancelled and refunded in full to their cards, and each guest gets an email. This cannot be undone.`
                            : 'Nobody has registered yet. The meal disappears from the app. This cannot be undone.'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setConfirmDelete(false)} disabled={saving} variant="outlined" sx={{ borderRadius: '20px', color: '#374151', borderColor: '#D1D5DB' }}>Keep</Button>
                    <Button onClick={remove} disabled={saving} variant="contained" color="error" sx={{ borderRadius: '20px' }}>
                        {saving ? 'Deleting…' : 'Delete meal'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};
